import { useMemo, useState } from "react";
import { Search, ChevronsUpDown, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

// Searchable pill-combobox matching the Coverage Map dropdowns.
// Pass either `options` (flat) or `groups` ([{label, options}]). Options: {value, label?, count?}.
export function SearchSelect({
  value,
  onChange,
  options,
  groups,
  allOption,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No match.",
  disabled = false,
  testid,
  className = "",
  align = "start",
}) {
  const [open, setOpen] = useState(false);
  const flat = useMemo(
    () => (groups ? groups.flatMap((g) => g.options) : (options || [])),
    [groups, options],
  );
  const total = flat.length + (allOption ? 1 : 0);
  const searchable = total > 6;
  const selected = flat.find((o) => String(o.value) === String(value ?? ""));
  const isAll = allOption && (value == null || value === "");
  const display = selected ? (selected.label ?? String(selected.value)) : (isAll ? allOption.label : placeholder);
  const isPlaceholder = !selected && !isAll;

  const pick = (v) => { onChange?.(v); setOpen(false); };

  const renderItem = (o) => {
    const active = String(o.value) === String(value ?? "");
    return (
      <CommandItem
        key={String(o.value)}
        value={`${o.label ?? o.value} ${o.value}`}
        data-testid={testid ? `${testid}-opt-${o.value}` : undefined}
        onSelect={() => pick(o.value)}
      >
        <Check className={`w-3.5 h-3.5 mr-2 shrink-0 ${active ? "opacity-100 text-accent" : "opacity-0"}`} />
        <span className="truncate">{o.label ?? o.value}</span>
        {o.count != null && (
          <span className="ml-auto font-mono-data text-xs text-muted-foreground">
            {typeof o.count === "number" ? o.count.toLocaleString() : o.count}
          </span>
        )}
      </CommandItem>
    );
  };

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm font-medium transition-colors hover:border-accent/50 disabled:opacity-50 disabled:cursor-not-allowed ${isPlaceholder ? "text-muted-foreground" : "text-foreground"} ${className}`}
        >
          <Search className="w-4 h-4 opacity-60 shrink-0" />
          <span className="flex-1 text-left truncate">{display}</span>
          <ChevronsUpDown className="w-3.5 h-3.5 opacity-60 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[12rem]" align={align}>
        <Command>
          {searchable && <CommandInput placeholder={searchPlaceholder} data-testid={testid ? `${testid}-search` : undefined} />}
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {allOption && <CommandGroup>{renderItem({ value: allOption.value ?? "", label: allOption.label })}</CommandGroup>}
            {groups
              ? groups.map((g) => (
                  <CommandGroup key={g.label} heading={g.label}>
                    {g.options.map(renderItem)}
                  </CommandGroup>
                ))
              : <CommandGroup>{flat.map(renderItem)}</CommandGroup>}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default SearchSelect;
